import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { merge } from 'rxjs';
import { EnfafDatetimePickerLightComponent } from '../../../../../../components/enfaf-datetime-picker-light/enfaf-datetime-picker-light.component';
import { EnfafIconComponent } from '../../../../../../components/enfaf-icon/enfaf-icon.component';
import { EnfafInputComponent } from '../../../../../../components/enfaf-input/enfaf-input.component';
import { EnfafSwitchDarkComponent } from '../../../../../../components/enfaf-switch-dark/enfaf-switch-dark.component';
import { MAX_EDITION_NUMBER, MIN_EDITION_NUMBER, VALIDITY_MAX_DATE, VALIDITY_MIN_DATE, parseEditionDateText } from './course-edition.utils';
import { EditionDateAdapter } from './edition-date-adapter';
import { FieldErrorA11yDirective } from './field-error-a11y.directive';

/** Valor de los selectores de vigencia: `DateTime` de Luxon en la zona de Madrid (calendario y texto tecleado). */
export type EditionDateValue = DateTime | Date | null;

/** Error key → clave i18n, en orden de prioridad. `serverError` lleva el mensaje del backend tal cual. */
const EDITION_NUMBER_ERRORS: Array<[string, string]> = [
  ['required', 'COURSE.EDITION.ERRORS.EDITION_NUMBER_REQUIRED'],
  ['positiveInteger', 'COURSE.EDITION.ERRORS.EDITION_NUMBER_INVALID'],
  ['editionNumberRange', 'COURSE.EDITION.ERRORS.EDITION_NUMBER_INVALID']
];

/**
 * Errors of a validity picker that come before «required» (D-06): typed text that is not a real date (31/02, 31/04…)
 * leaves the control empty, so it also has `required`; the admin must read «Fecha no válida», not «Indica…».
 * `matDatepickerParse` and `matDatetimePicker{Min,Max}` come from the picker input's own validators
 * (`NgxMatDatepickerInput` of @ngxmc/datetime-picker: unparsable text, calendar min/max).
 */
const VALIDITY_DATE_ERRORS: Array<[string, string]> = [
  ['matDatepickerParse', 'COURSE.EDITION.ERRORS.INVALID_DATE'],
  ['validityYearRange', 'COURSE.EDITION.ERRORS.VALIDITY_YEAR_RANGE'],
  ['matDatetimePickerMin', 'COURSE.EDITION.ERRORS.VALIDITY_YEAR_RANGE'],
  ['matDatetimePickerMax', 'COURSE.EDITION.ERRORS.VALIDITY_YEAR_RANGE']
];
const VALID_FROM_ERRORS: Array<[string, string]> = [...VALIDITY_DATE_ERRORS, ['required', 'COURSE.EDITION.ERRORS.VALID_FROM_REQUIRED']];
const VALID_UNTIL_ERRORS: Array<[string, string]> = [
  ...VALIDITY_DATE_ERRORS,
  ['required', 'COURSE.EDITION.ERRORS.VALID_UNTIL_REQUIRED'],
  ['validityRange', 'COURSE.EDITION.ERRORS.VALIDITY_RANGE']
];

/**
 * ENF-2372 — bloque de edición del modal de curso (modelo del 15-oct):
 * - en una edición (la que se crea con «Crear nueva edición» o una ya guardada): interruptor «Es sucesor de otro
 *   curso» marcado y bloqueado, máster raíz de solo lectura (no se cambia nunca tras crearla) y número de edición;
 * - en cualquier Máster: la vigencia de compra (obligatoria solo al crear una edición).
 *
 * Es presentacional: los controles pertenecen al `courseForm` del modal (que es quien guarda) y la orquestación
 * (precarga, nombre generado) vive en el modal. Este componente solo decide qué se pinta y qué error se muestra.
 */
@Component({
  selector: 'app-course-edition-fields',
  imports: [
    TranslatePipe,
    MatTooltipModule,
    EnfafSwitchDarkComponent,
    EnfafIconComponent,
    EnfafInputComponent,
    EnfafDatetimePickerLightComponent,
    FieldErrorA11yDirective
  ],
  // QA-A-03: the validity pickers build and paint their dates in Madrid, never in the browser's zone.
  providers: [{ provide: DateAdapter, useClass: EditionDateAdapter }],
  templateUrl: './course-edition-fields.component.html',
  styleUrl: './course-edition-fields.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CourseEditionFieldsComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);

  readonly isSuccessorControl = input.required<FormControl<boolean | null>>();
  readonly editionNumberControl = input.required<FormControl<number | string | null>>();
  readonly validFromControl = input.required<FormControl<EditionDateValue>>();
  readonly validUntilControl = input.required<FormControl<EditionDateValue>>();

  /** God y el curso es una edición Máster: interruptor bloqueado, raíz de solo lectura y número (D-10). */
  readonly showEditionControls = input(false);
  /** Nombre del Máster de la raíz, que se muestra de solo lectura. */
  readonly rootName = input<string | null>(null);
  /** God y modalidad Máster (raíz o edición): vigencia de compra (A.3). */
  readonly showValidity = input(false);
  // La obligatoriedad de la vigencia (solo al crear una edición, A.3/D-3) la marcan los
  // validadores del modal; mat-form-field añade el asterisco al ver `Validators.required`.
  readonly editMode = input(false);
  readonly isLoadingPrefill = input(false);

  /** Typed validity text → Madrid `DateTime` (QA-A-03), for `enfaf-datetime-picker-light[manualInputParser]`. */
  readonly parseValidityText = parseEditionDateText;
  /** Same upper bound as the server (D-05): the native `max` of «Edición». */
  readonly maxEditionNumber = MAX_EDITION_NUMBER;
  /** The 1st edition is the root: an edition starts at 2. */
  readonly minEditionNumber = MIN_EDITION_NUMBER;
  /** Calendar limits of the validity pickers (D-06): years 1970–2100, like the server. */
  readonly validityMinDate = VALIDITY_MIN_DATE.toJSDate();
  readonly validityMaxDate = VALIDITY_MAX_DATE.toJSDate();
  /** Read-only «Máster raíz»: the root is fixed once the edition exists (a wrong one is deleted and recreated). */
  readonly rootNameControl = new FormControl<string | null>(null);

  constructor() {
    // OnPush: los errores y valores de estos controles los cambia el modal (servidor,
    // precarga) sin pasar por un input; se repinta con cada evento del control.
    effect((onCleanup) => {
      const controls: AbstractControl[] = [this.isSuccessorControl(), this.editionNumberControl(), this.validFromControl(), this.validUntilControl()];
      const subscription = merge(...controls.map((control) => control.events)).subscribe(() => this.cdr.markForCheck());
      onCleanup(() => subscription.unsubscribe());
    });
    effect(() => this.rootNameControl.setValue(this.rootName()));

    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => this.cdr.markForCheck());
  }

  get editionNumberError(): string {
    return this.errorText(this.editionNumberControl(), EDITION_NUMBER_ERRORS);
  }

  get validFromError(): string {
    return this.errorText(this.validFromControl(), VALID_FROM_ERRORS);
  }

  get validUntilError(): string {
    return this.errorText(this.validUntilControl(), VALID_UNTIL_ERRORS);
  }

  /**
   * The wrapped field paints its `mat-error` (and the error state) once touched or edited: `aria-invalid` follows
   * the same rule so it is never announced for a message that is not on screen (D-4).
   */
  showsError(control: AbstractControl, message: string): boolean {
    return !!message && (control.touched || control.dirty);
  }

  private errorText(control: AbstractControl, messages: Array<[string, string]>): string {
    if (control.hasError('serverError')) return String(control.getError('serverError'));
    const match = messages.find(([errorKey]) => control.hasError(errorKey));
    return match ? this.translate.instant(match[1]) : '';
  }
}
